/**
 * =================================================================
 *   THIS IS THE CustomerShowcase COMPONENT (All-in-One, No Installs)
 * =================================================================
 *   • Displays a responsive grid of customer stories.
 *   • Includes an infinitely scrolling brand marquee using only CSS.
 *   • Self-contained with unique, prefixed class names (cs-).
 * =================================================================
 */
import React from 'react';
import './CustomerShowcase.css';

// --- Component Data ---

// The 12 specific brand logos from your HTML
const brandLogos = [
  { alt: 'Lotte', src: 'https://sendbird.imgix.net/cms/Lotte_2025-06-13-092924_jbbj.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'Redfin', src: 'https://sendbird.imgix.net/cms/Redfin_2025-06-13-092928_gltx.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'Teladoc', src: 'https://sendbird.imgix.net/cms/Teladoc_2025-06-13-092931_awmn.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'Noom', src: 'https://sendbird.imgix.net/cms/Noom_2025-06-13-092928_jket.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'Match group', src: 'https://sendbird.imgix.net/cms/Match-group_2025-06-13-092926_aaxk.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'Hyundai', src: 'https://sendbird.imgix.net/cms/Hyundai_2025-06-13-092924_sspn.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'Hinge', src: 'https://sendbird.imgix.net/cms/Hinge_2025-06-13-092922_uoxx.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'SK telecom', src: 'https://sendbird.imgix.net/cms/SK-telecom_2025-06-13-092930_mnfn.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'Virgin mobile', src: 'https://sendbird.imgix.net/cms/Virgin-mobile_2025-06-13-092933_qeyt.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'Weight Watchers', src: 'https://sendbird.imgix.net/cms/WeightWatchers_2025-06-13-092935_oxvf.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'WEF', src: 'https://sendbird.imgix.net/cms/WEF_2025-06-13-092934_qqnw.svg?auto=format,compress&crop=faces&h=500' },
  { alt: 'Maya', src: 'https://sendbird.imgix.net/cms/Maya_2025-06-13-092927_giqr.svg?auto=format,compress&crop=faces&h=500' },
];

const customerCardData = [
    { id: 'porter', bgImage: 'https://sendbird.imgix.net/cms/shutterstock_2523601557-1_2025-06-17-071544_jgga.png?wm=webp', logo: 'https://sendbird.imgix.net/cms/Porter_logo_2025-06-30-173151_khsc.svg', logoPosition: 'cs-logo-top-left' },
    { id: 'maya', bgColor: '#f7f5ff', logo: 'https://sendbird.imgix.net/cms/maya_logo_2025-06-30-174447_psuk.svg', logoPosition: 'cs-logo-top-center', stat: '56%', description: 'Cost reduction on support cases', textColor: '#000' },
    { id: 'miso', bgImage: 'https://sendbird.imgix.net/cms/shutterstock_2335214019-1_2025-06-17-073331_sfew.png?wm=webp', logo: 'https://sendbird.imgix.net/cms/miso-1x.png', logoPosition: 'cs-logo-top-left-text' },
    { id: 'hinge', bgImage: 'https://sendbird.imgix.net/cms/shutterstock_1710037585-1_2025-06-17-073502_uvss.png?wm=webp', logo: 'https://sendbird.imgix.net/cms/hinge-customer.svg', logoPosition: 'cs-logo-top-left-text' },
    { id: 'gather', bgColor: '#000', logo: 'https://sendbird.imgix.net/cms/gther_2025-06-30-173152_urga.svg', logoPosition: 'cs-logo-top-left', stat: '70%', description: 'Queries automatically answered', textColor: '#fff' },
    { id: 'paytm', bgImage: 'https://sendbird.imgix.net/cms/shutterstock_404730085-1_2025-06-17-073522_zktj.png?wm=webp', logo: 'https://sendbird.imgix.net/cms/paytm.svg', logoPosition: 'cs-logo-top-right-text' },
    { id: 'redfin', bgImage: 'https://sendbird.imgix.net/cms/shutterstock_2560692043-1_2025-06-17-073538_pywm.png?wm=webp', logo: 'https://sendbird.imgix.net/cms/redfin_2025-06-30-173638_ujvx.svg', logoPosition: 'cs-logo-top-left-text', logoFilter: 'brightness(0) invert(1)' },
    // The "virgin" object that created the 96% box has been deleted from this array.
];

const CustomerCard = ({ cardData }) => {
  const cardStyle = { backgroundColor: cardData.bgColor || 'transparent', backgroundImage: cardData.bgImage ? `url(${cardData.bgImage})` : 'none', color: cardData.textColor || '#000' };
  const logoStyle = { filter: cardData.logoFilter || 'none' };
  const cardClassName = `cs-customer-card cs-area-${cardData.id}`;

  return (
    <div className={cardClassName} style={cardStyle}>
      {cardData.logo && ( <div className={`cs-card-logo-wrapper ${cardData.logoPosition}`}> <img src={cardData.logo} alt={`${cardData.id} logo`} className="cs-card-logo" style={logoStyle} /> </div> )}
      {cardData.stat && ( <div className="cs-card-stats"> <p className="cs-stat-number">{cardData.stat}</p> <p className="cs-stat-description">{cardData.description}</p> </div> )}
    </div>
  );
};

function CustomerShowcase() {
  return (
    <div className="cs-showcase-container">
      <div className="cs-showcase-content">
        <header className="cs-showcase-title-header">
          <h1>The world's leading brands build on Sendbird</h1>
        </header>
        
        <div className="cs-brand-marquee">
          <div className="cs-marquee-track">
            {[...brandLogos, ...brandLogos].map((brand, index) => (
              <div key={index} className="cs-marquee-logo">
                <img src={brand.src} alt={brand.alt} />
              </div>
            ))}
          </div>
        </div>
        
        <main className="cs-customer-grid">
          {customerCardData.map((card) => (
            <CustomerCard key={card.id} cardData={card} />
          ))}
        </main>
      </div>
    </div>
  );
}

export default CustomerShowcase;